/**
 * WeaveIn.ai Platform - Emergency Situation Room
 * Full-page command center for managing emergency incidents.
 * Layout: Stats cards (top), Wave status (left), Safety feed (right),
 * Non-responder list (bottom).
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Shield,
  Phone,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Radio,
  ChevronDown,
  Send,
  ArrowRight,
  RefreshCw,
  MapPin,
  Megaphone,
  HelpCircle,
  Home,
} from 'lucide-react';
import {
  emergencyAPI,
  IncidentDetail,
  WaveStatus,
  SafetyResponse,
  NonResponder,
} from '@/services/api/emergency';

// ============================================================================
// STAT CARD
// ============================================================================

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}> = ({ label, value, icon, color, subtitle }) => (
  <div className={`rounded-lg border p-3 ${color}`}>
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-xs font-medium text-gray-600">{label}</span>
    </div>
    <div className="text-2xl font-bold">{value}</div>
    {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
  </div>
);

// ============================================================================
// SEVERITY BADGE
// ============================================================================

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => {
  const config: Record<string, string> = {
    advisory: 'bg-blue-100 text-blue-700',
    watch: 'bg-yellow-100 text-yellow-700',
    warning: 'bg-orange-100 text-orange-700',
    emergency: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config[severity] || config.watch}`}>
      {severity.toUpperCase()}
    </span>
  );
};

// ============================================================================
// WAVE STATUS TIMELINE
// ============================================================================

const WaveTimeline: React.FC<{
  waves: WaveStatus[];
  onAdvanceWave: (waveId: number) => void;
  isAdvancing: boolean;
}> = ({ waves, onAdvanceWave, isAdvancing }) => {
  if (waves.length === 0) {
    return <div className="text-sm text-gray-500 py-4 text-center">No waves configured</div>;
  }

  const channelLabels: Record<string, string> = {
    sms: 'SMS Blast',
    voice_ai: 'Voice AI Calls',
    email: 'Email',
    whatsapp: 'WhatsApp',
    case_manager_task: 'Case Manager Tasks',
  };

  const statusColors: Record<string, string> = {
    pending: 'border-gray-300 bg-gray-50',
    in_progress: 'border-blue-400 bg-blue-50',
    completed: 'border-green-400 bg-green-50',
    cancelled: 'border-gray-300 bg-gray-100',
  };

  return (
    <div className="space-y-3">
      {waves.map((wave, i) => (
        <div
          key={wave.wave_id}
          className={`border rounded-lg p-3 ${statusColors[wave.status] || statusColors.pending}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-700">Wave {wave.wave_number}</span>
              <span className="text-xs text-gray-500">{channelLabels[wave.channel] || wave.channel}</span>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded ${
              wave.status === 'completed' ? 'bg-green-200 text-green-800' :
              wave.status === 'in_progress' ? 'bg-blue-200 text-blue-800' :
              'bg-gray-200 text-gray-700'
            }`}>
              {wave.status}
            </span>
          </div>

          {/* Progress bar */}
          {wave.target_count > 0 && (
            <div className="mb-2">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-blue-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.round(((wave.sent_count + wave.responded_count) / wave.target_count) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Stats row */}
          <div className="flex gap-3 text-xs text-gray-600">
            <span>Sent: <b>{wave.sent_count}</b></span>
            <span>Responded: <b className="text-green-600">{wave.responded_count}</b></span>
            <span>Failed: <b className="text-red-600">{wave.failed_count}</b></span>
          </div>

          {/* Delay info */}
          {wave.delay_after_previous && wave.status === 'pending' && (
            <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>Starts {wave.delay_after_previous} after previous wave</span>
            </div>
          )}

          {/* Advance button for pending waves */}
          {wave.status === 'pending' && i > 0 && waves[i - 1].status === 'completed' && (
            <button
              onClick={() => onAdvanceWave(wave.wave_id)}
              disabled={isAdvancing}
              className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              <ArrowRight className="w-3 h-3" />
              {isAdvancing ? 'Advancing...' : 'Start Wave Now'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// SAFETY RESPONSE FEED
// ============================================================================

const SafetyFeed: React.FC<{ responses: SafetyResponse[] }> = ({ responses }) => {
  const typeConfig: Record<string, { icon: React.ReactNode; color: string }> = {
    safe: { icon: <CheckCircle className="w-3.5 h-3.5 text-green-500" />, color: 'bg-green-50 border-green-200' },
    needs_help: { icon: <AlertTriangle className="w-3.5 h-3.5 text-red-500" />, color: 'bg-red-50 border-red-200' },
    shelter: { icon: <Home className="w-3.5 h-3.5 text-orange-500" />, color: 'bg-orange-50 border-orange-200' },
    evacuated: { icon: <MapPin className="w-3.5 h-3.5 text-blue-500" />, color: 'bg-blue-50 border-blue-200' },
    unknown: { icon: <HelpCircle className="w-3.5 h-3.5 text-gray-400" />, color: 'bg-gray-50 border-gray-200' },
  };

  return (
    <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
      {responses.length === 0 && (
        <div className="text-sm text-gray-400 text-center py-6">No responses yet</div>
      )}
      {responses.map((r) => {
        const cfg = typeConfig[r.response_type] || typeConfig.unknown;
        return (
          <div
            key={r.id}
            className={`flex items-center gap-2 px-3 py-2 border rounded-md text-xs ${cfg.color}`}
          >
            {cfg.icon}
            <div className="flex-1 min-w-0">
              <span className="font-medium text-gray-800">{r.member_name}</span>
              {r.zip_code && <span className="text-gray-400 ml-1">({r.zip_code})</span>}
              {r.keyword && (
                <span className="ml-1.5 text-gray-500">"{r.keyword}"</span>
              )}
            </div>
            <span className="text-gray-400 whitespace-nowrap">
              {r.responded_at ? new Date(r.responded_at).toLocaleTimeString() : ''}
            </span>
            {r.needs_followup && (
              <span className="px-1.5 py-0.5 bg-red-200 text-red-700 rounded text-[10px] font-medium">
                FOLLOWUP
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// NON-RESPONDER TABLE
// ============================================================================

const NonResponderTable: React.FC<{ members: NonResponder[] }> = ({ members }) => (
  <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
    <table className="w-full text-xs">
      <thead className="bg-gray-50 sticky top-0">
        <tr>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Phone</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">ZIP</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Engagement</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Churn Risk</th>
          <th className="px-3 py-2 text-left font-medium text-gray-600">Waves</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {members.map((m) => (
          <tr key={m.member_id} className="hover:bg-gray-50">
            <td className="px-3 py-2 font-medium text-gray-900">
              {m.first_name} {m.last_name}
            </td>
            <td className="px-3 py-2 text-gray-600">{m.phone || '—'}</td>
            <td className="px-3 py-2 text-gray-600">{m.zip_code || '—'}</td>
            <td className="px-3 py-2">
              <span className={`font-medium ${
                m.engagement_score >= 70 ? 'text-green-600' :
                m.engagement_score >= 40 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {m.engagement_score.toFixed(0)}
              </span>
            </td>
            <td className="px-3 py-2">
              <div className="flex items-center gap-1">
                <div className="w-12 h-1.5 bg-gray-200 rounded-full">
                  <div
                    className={`h-1.5 rounded-full ${
                      m.churn_score >= 0.7 ? 'bg-red-500' :
                      m.churn_score >= 0.4 ? 'bg-orange-500' :
                      'bg-green-500'
                    }`}
                    style={{ width: `${m.churn_score * 100}%` }}
                  />
                </div>
                <span className="text-gray-600">{(m.churn_score * 100).toFixed(0)}%</span>
              </div>
            </td>
            <td className="px-3 py-2 text-gray-600">{m.waves_attempted}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {members.length === 0 && (
      <div className="text-center py-6 text-sm text-gray-400">All members have responded</div>
    )}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface EmergencySituationRoomProps {
  incidentId: number;
  onBack?: () => void;
  onLaunchCampaign?: (incidentId: number, incidentName: string) => void;
}

export const EmergencySituationRoom: React.FC<EmergencySituationRoomProps> = ({
  incidentId,
  onBack,
  onLaunchCampaign,
}) => {
  const queryClient = useQueryClient();

  // Fetch incident detail (with auto-refresh)
  const { data, isLoading, error } = useQuery({
    queryKey: ['emergency-incident', incidentId],
    queryFn: () => emergencyAPI.getIncident(incidentId),
    refetchInterval: 30000,
    enabled: incidentId > 0,
  });

  // Fetch safety feed
  const { data: feedData } = useQuery({
    queryKey: ['safety-feed', incidentId],
    queryFn: () => emergencyAPI.getSafetyFeed(incidentId, 50),
    refetchInterval: 15000,
    enabled: incidentId > 0,
  });

  // Fetch non-responders
  const { data: nonResponderData } = useQuery({
    queryKey: ['non-responders', incidentId],
    queryFn: () => emergencyAPI.getNonResponders(incidentId),
    refetchInterval: 30000,
    enabled: incidentId > 0,
  });

  // Wave advance mutation
  const advanceWaveMutation = useMutation({
    mutationFn: (waveId: number) => emergencyAPI.advanceWave(waveId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emergency-incident', incidentId] });
    },
  });

  // Resolve incident mutation
  const resolveMutation = useMutation({
    mutationFn: () => emergencyAPI.updateIncident(incidentId, {
      status: 'resolved',
      resolution_notes: 'Resolved from situation room',
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emergency-incident', incidentId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-500">
        Failed to load incident data
      </div>
    );
  }

  const { incident, waves, safety_status } = data;
  const responses = feedData?.responses || [];
  const nonResponders = nonResponderData?.non_responders || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Bar */}
      <div className={`px-6 py-3 flex items-center justify-between ${
        incident.severity === 'emergency' ? 'bg-red-600 text-white' :
        incident.severity === 'warning' ? 'bg-orange-500 text-white' :
        'bg-yellow-400 text-gray-900'
      }`}>
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="opacity-80 hover:opacity-100">
              &larr; Back
            </button>
          )}
          <Megaphone className="w-5 h-5" />
          <h1 className="text-lg font-bold">{incident.incident_name}</h1>
          <SeverityBadge severity={incident.severity} />
          <span className={`text-xs px-2 py-0.5 rounded ${
            incident.status === 'active' ? 'bg-white/20' : 'bg-white/40'
          }`}>
            {incident.status.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs opacity-80">
            Auto-refreshing every 30s
          </span>
          {incident.status === 'active' && onLaunchCampaign && (
            <button
              onClick={() => onLaunchCampaign(incidentId, incident.incident_name)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-red-700 hover:bg-white/90 rounded text-xs font-bold shadow-sm"
            >
              <Megaphone className="w-3.5 h-3.5" />
              {waves.length === 0 ? 'Launch Campaign' : 'New Campaign'}
            </button>
          )}
          {incident.status === 'active' && (
            <button
              onClick={() => resolveMutation.mutate()}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium"
            >
              Resolve Incident
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard
          label="Total Targeted"
          value={safety_status.total_targeted}
          icon={<Users className="w-4 h-4 text-gray-500" />}
          color="bg-white border"
        />
        <StatCard
          label="Confirmed Safe"
          value={safety_status.confirmed_safe}
          icon={<CheckCircle className="w-4 h-4 text-green-500" />}
          color="bg-white border"
          subtitle={safety_status.total_targeted > 0 ? `${(safety_status.response_rate * 100).toFixed(0)}% response rate` : undefined}
        />
        <StatCard
          label="Needs Help"
          value={safety_status.needs_help}
          icon={<AlertTriangle className="w-4 h-4 text-red-500" />}
          color={safety_status.needs_help > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border'}
        />
        <StatCard
          label="Shelter/Evacuated"
          value={(safety_status.shelter || 0) + (safety_status.evacuated || 0)}
          icon={<Home className="w-4 h-4 text-orange-500" />}
          color="bg-white border"
        />
        <StatCard
          label="Unreachable"
          value={safety_status.unreachable}
          icon={<XCircle className="w-4 h-4 text-gray-400" />}
          color="bg-white border"
        />
        <StatCard
          label="Active Waves"
          value={waves.filter(w => w.status === 'in_progress').length}
          icon={<Radio className="w-4 h-4 text-blue-500" />}
          color="bg-white border"
          subtitle={`${waves.length} total waves`}
        />
      </div>

      {/* Empty state: no campaign launched yet */}
      {waves.length === 0 && safety_status.total_targeted === 0 && incident.status === 'active' && (
        <div className="mx-6 mt-2 mb-4 p-6 bg-white border-2 border-dashed border-orange-300 rounded-lg text-center">
          <Megaphone className="w-10 h-10 text-orange-400 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">No Campaign Launched Yet</h3>
          <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
            This incident has been created but no outreach campaign has been launched.
            Configure your audience, message templates, and escalation waves to begin emergency communications.
          </p>
          {onLaunchCampaign && (
            <button
              onClick={() => onLaunchCampaign(incidentId, incident.incident_name)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 shadow-sm"
            >
              <Send className="w-4 h-4" />
              Configure & Launch Campaign
            </button>
          )}
          <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Select target audience</span>
            <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Configure wave escalation</span>
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Track safety responses</span>
          </div>
        </div>
      )}

      {/* Main Content: 2-column layout */}
      <div className="px-6 pb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Wave Escalation */}
        <div className="bg-white border rounded-lg">
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold">Wave Escalation</h2>
          </div>
          <div className="p-4">
            <WaveTimeline
              waves={waves}
              onAdvanceWave={(waveId) => advanceWaveMutation.mutate(waveId)}
              isAdvancing={advanceWaveMutation.isPending}
            />
          </div>
        </div>

        {/* Right: Safety Response Feed */}
        <div className="bg-white border rounded-lg">
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-600" />
            <h2 className="text-sm font-semibold">Safety Check-In Feed</h2>
            <span className="text-xs text-gray-400 ml-auto">Live</span>
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          </div>
          <div className="p-4">
            <SafetyFeed responses={responses} />
          </div>
        </div>
      </div>

      {/* Bottom: Non-Responder Table */}
      <div className="px-6 pb-6">
        <div className="bg-white border rounded-lg">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-red-600" />
              <h2 className="text-sm font-semibold">
                Non-Responders ({nonResponders.length})
              </h2>
              <span className="text-xs text-gray-500">Sorted by churn risk (hardest to reach first)</span>
            </div>
          </div>
          <NonResponderTable members={nonResponders} />
        </div>
      </div>

      {/* Timeline */}
      {data.timeline && data.timeline.length > 0 && (
        <div className="px-6 pb-6">
          <div className="bg-white border rounded-lg">
            <div className="px-4 py-3 border-b flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-600" />
              <h2 className="text-sm font-semibold">Incident Timeline</h2>
            </div>
            <div className="p-4 space-y-2 max-h-48 overflow-y-auto">
              {data.timeline.map((event, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-gray-400 whitespace-nowrap min-w-[70px]">
                    {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}
                  </span>
                  <span className="text-gray-700">{event.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencySituationRoom;
